import { useEffect } from 'react'
import { Icon, SlimButton } from 'components'

import * as S from './styles'

export interface ModalOptions {
  id?: number | string
  open: boolean
  sending?: boolean
}

export interface ModalProps {
  open: boolean
  setOpen: (isOpen: boolean) => void
  showClose?: boolean
  closeAction?: () => void
  title?: string | JSX.Element
  description?: string | JSX.Element
  children?: React.ReactNode
  showReturn?: boolean
  returnIcon?: React.ReactNode
  returnText?: string
  returnAction?: () => void
  returnDisabled?: boolean
  showAction?: boolean
  buttonIcon?: React.ReactNode
  buttonText?: string
  buttonAction?: () => void
  buttonDisabled?: boolean
  buttonVariant?: 'primary' | 'danger'
  alignContent?: 'flex-start' | 'center' | 'flex-end'
  titleSize?: number
  descriptionSize?: number
  modalWidth?: number
  loading?: boolean
}

export const Modal: React.FC<ModalProps> = ({
  open,
  setOpen,
  showClose = true,
  closeAction = () => {
    setOpen(false)
  },
  title,
  description,
  children,
  showReturn = false,
  returnIcon = <Icon icon="undo" size="15" />,
  returnText = 'Voltar',
  returnAction = () => {
    setOpen(false)
  },
  returnDisabled = false,
  showAction = true,
  buttonIcon,
  buttonText,
  buttonAction = () => {
    setOpen(false)
  },
  buttonDisabled,
  buttonVariant = 'primary',
  alignContent = 'center',
  titleSize = 22,
  descriptionSize = 14,
  modalWidth,
  loading = false
}) => {
  useEffect(() => {
    function handleCloseByKeyUpEscape(event: KeyboardEvent) {
      if (event.key === 'Escape' && open) {
        closeAction()
      }
    }

    window.addEventListener('keyup', handleCloseByKeyUpEscape)

    return () => {
      window.removeEventListener('keyup', handleCloseByKeyUpEscape)
    }
  }, [open, closeAction])

  const hasTitle = title && title !== ''

  return (
    <>
      {open && (
        <S.Wrapper onClick={closeAction} data-testid="wrapper-modal">
          <S.Content
            width={modalWidth}
            alignContent={alignContent}
            onClick={(event) => event.stopPropagation()}
          >
            {hasTitle || showClose ? (
              <S.Texts>
                <S.TitleRow>
                  {hasTitle ? (
                    typeof title === 'string' ? (
                      <S.Title textSize={titleSize}>{title}</S.Title>
                    ) : (
                      <>{title}</>
                    )
                  ) : (
                    <div />
                  )}
                  {showClose ? (
                    <S.CloseButton onClick={closeAction}>
                      <Icon icon="close" size="28" />
                    </S.CloseButton>
                  ) : null}
                </S.TitleRow>
                <S.Divider />
                {!!description && (
                  <S.Description textSize={descriptionSize}>{description}</S.Description>
                )}
              </S.Texts>
            ) : null}
            {children}
            {showAction && (
              <>
                <S.Divider />
                <S.Actions>
                  {showReturn && (
                    <SlimButton
                      variant="ghost"
                      data-testid="btn-return"
                      content={returnText}
                      disabled={returnDisabled}
                      onClick={returnAction}
                      icon={returnIcon}
                      loading={loading}
                    />
                  )}
                  <div className="spacer" />
                  {buttonText && (
                    <SlimButton
                      variant={buttonVariant}
                      data-testid="btn-action"
                      content={buttonText}
                      disabled={buttonDisabled}
                      onClick={buttonAction}
                      icon={buttonIcon}
                      loading={loading}
                    />
                  )}
                </S.Actions>
              </>
            )}
          </S.Content>
        </S.Wrapper>
      )}
    </>
  )
}
